import React, { useState, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function CalendarioContas({ contasPagar = [], contasReceber = [] }) {
  const [mesAtual, setMesAtual] = useState(new Date());

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(value || 0);
  };

  const diasDoMes = useMemo(() => {
    const inicio = startOfMonth(mesAtual);
    const fim = endOfMonth(mesAtual);
    return eachDayOfInterval({ start: inicio, end: fim });
  }, [mesAtual]);

  // Preencher dias antes do início do mês para alinhar a grade
  const primeiroDiaSemana = startOfMonth(mesAtual).getDay();
  const diasAnteriores = Array(primeiroDiaSemana).fill(null);

  // Agrupar contas por dia
  const contasPorDia = useMemo(() => {
    const mapa = {};
    
    contasPagar.forEach(conta => {
      if (!conta.data_vencimento) return;
      const dataStr = conta.data_vencimento;
      if (!mapa[dataStr]) mapa[dataStr] = { pagar: [], receber: [] };
      mapa[dataStr].pagar.push(conta);
    });

    contasReceber.forEach(conta => {
      const dataStr = conta.data_vencimento || conta.data;
      if (!dataStr) return;
      if (!mapa[dataStr]) mapa[dataStr] = { pagar: [], receber: [] };
      mapa[dataStr].receber.push(conta);
    });

    return mapa;
  }, [contasPagar, contasReceber]);

  // Totais do mês
  const totaisMes = useMemo(() => {
    let totalPagar = 0;
    let totalReceber = 0;

    contasPagar.forEach(conta => {
      if (!conta.data_vencimento || conta.status === 'pago') return;
      const data = parseISO(conta.data_vencimento);
      if (isSameMonth(data, mesAtual)) {
        totalPagar += conta.valor || 0;
      }
    });

    contasReceber.forEach(conta => {
      const dataStr = conta.data_vencimento || conta.data;
      if (!dataStr || conta.status === 'recebido' || conta.status === 'pago') return;
      const data = parseISO(dataStr);
      if (isSameMonth(data, mesAtual)) {
        totalReceber += conta.valor || 0;
      }
    });

    return { totalPagar, totalReceber, saldo: totalReceber - totalPagar };
  }, [contasPagar, contasReceber, mesAtual]);

  const renderDia = (dia) => {
    if (!dia) return <div className="h-24 bg-stone-50" />;

    const dataStr = format(dia, 'yyyy-MM-dd');
    const contas = contasPorDia[dataStr] || { pagar: [], receber: [] };
    const totalPagar = contas.pagar.filter(c => c.status !== 'pago').reduce((acc, c) => acc + (c.valor || 0), 0);
    const totalReceber = contas.receber.filter(c => c.status !== 'pago').reduce((acc, c) => acc + (c.valor || 0), 0);
    const temContas = contas.pagar.length > 0 || contas.receber.length > 0;
    const hoje = isToday(dia);
    const mesmoMes = isSameMonth(dia, mesAtual);

    return (
      <Popover key={dataStr}>
        <PopoverTrigger asChild>
          <button
            className={`h-24 p-1 border border-stone-100 transition-all hover:bg-stone-50 text-left flex flex-col
              ${!mesmoMes ? 'bg-stone-50 opacity-50' : 'bg-white'}
              ${hoje ? 'ring-2 ring-indigo-500 ring-inset' : ''}
              ${temContas ? 'cursor-pointer' : 'cursor-default'}
            `}
          >
            <span className={`text-sm font-medium px-1 ${hoje ? 'bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-stone-600'}`}>
              {format(dia, 'd')}
            </span>
            
            <div className="flex-1 overflow-hidden space-y-0.5 mt-1">
              {totalReceber > 0 && (
                <div className="flex items-center gap-1 px-1 py-0.5 bg-green-100 rounded text-xs">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-green-700 font-medium truncate">{formatCurrency(totalReceber)}</span>
                </div>
              )}
              {totalPagar > 0 && (
                <div className="flex items-center gap-1 px-1 py-0.5 bg-red-100 rounded text-xs">
                  <TrendingDown className="w-3 h-3 text-red-600" />
                  <span className="text-red-700 font-medium truncate">{formatCurrency(totalPagar)}</span>
                </div>
              )}
            </div>

            {temContas && (
              <div className="flex gap-1 px-1">
                {contas.pagar.length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-red-500" title={`${contas.pagar.length} conta(s) a pagar`} />
                )}
                {contas.receber.length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-green-500" title={`${contas.receber.length} receita(s)`} />
                )}
              </div>
            )}
          </button>
        </PopoverTrigger>

        {temContas && (
          <PopoverContent className="w-80 p-0" align="start">
            <div className="p-3 border-b bg-stone-50">
              <p className="font-semibold text-stone-800">
                {format(dia, "dd 'de' MMMM", { locale: ptBR })}
              </p>
            </div>
            <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
              {contas.receber.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    A Receber
                  </p>
                  {contas.receber.map((c, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-stone-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-stone-800">{c.descricao || c.categoria}</p>
                        <p className="text-xs text-stone-500">{c.fornecedor_cliente}</p>
                      </div>
                      <span className="text-green-600 font-semibold">+{formatCurrency(c.valor)}</span>
                    </div>
                  ))}
                </div>
              )}
              {contas.pagar.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" />
                    A Pagar
                  </p>
                  {contas.pagar.map((c, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-stone-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-stone-800">{c.descricao}</p>
                        <p className="text-xs text-stone-500">{c.fornecedor}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-red-600 font-semibold">-{formatCurrency(c.valor)}</span>
                        {c.status === 'pago' && <Badge className="ml-1 bg-green-100 text-green-700 text-xs">Pago</Badge>}
                        {c.status === 'atrasado' && <Badge className="ml-1 bg-red-100 text-red-700 text-xs">Atrasado</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  };

  return (
    <Card className="bg-white border-stone-200">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Calendário Financeiro
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setMesAtual(subMonths(mesAtual, 1))}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="font-semibold text-stone-800 min-w-[140px] text-center">
              {format(mesAtual, "MMMM yyyy", { locale: ptBR })}
            </span>
            <Button variant="ghost" size="icon" onClick={() => setMesAtual(addMonths(mesAtual, 1))}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Resumo do mês */}
        <div className="flex gap-4 mt-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-lg">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700">Receber: <strong>{formatCurrency(totaisMes.totalReceber)}</strong></span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-lg">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-700">Pagar: <strong>{formatCurrency(totaisMes.totalPagar)}</strong></span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${totaisMes.saldo >= 0 ? 'bg-blue-100' : 'bg-amber-100'}`}>
            <span className={`text-sm ${totaisMes.saldo >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
              Saldo: <strong>{formatCurrency(totaisMes.saldo)}</strong>
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Cabeçalho dos dias da semana */}
        <div className="grid grid-cols-7 mb-1">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dia => (
            <div key={dia} className="text-center text-xs font-semibold text-stone-500 py-2">
              {dia}
            </div>
          ))}
        </div>

        {/* Grade do calendário */}
        <div className="grid grid-cols-7 border-t border-l border-stone-200">
          {diasAnteriores.map((_, i) => (
            <div key={`empty-${i}`} className="h-24 bg-stone-50 border-r border-b border-stone-200" />
          ))}
          {diasDoMes.map(dia => (
            <div key={format(dia, 'yyyy-MM-dd')} className="border-r border-b border-stone-200">
              {renderDia(dia)}
            </div>
          ))}
        </div>

        {/* Legenda */}
        <div className="flex items-center gap-4 mt-4 text-xs text-stone-500">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500" />
            <span>Receitas</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <span>Despesas</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs">H</span>
            <span>Hoje</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}